import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import apiClient, { resolveBaseURL } from "../../../utils/apiClient";
import { hasPermission, PERMISSIONS } from "../../../utils/permissions";
import Loader from "../../Loader";

const STATUS_OPTIONS = ["Pending", "Approved", "Rejected", "All"];

const ListSchoolSlotPurchases = () => {
  const { user } = useSelector((state) => state.auth || {});
  const canReview = hasPermission(user, PERMISSIONS.SCHOOLS_PAYMENTS_REVIEW);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("Pending");
  const [actionId, setActionId] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [rejectingPurchase, setRejectingPurchase] = useState(null);

  const loadPurchases = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = statusFilter === "All" ? {} : { status: statusFilter };
      const { data } = await apiClient.get("/admin/school-slot-purchases", { params });
      setPurchases(Array.isArray(data?.purchases) ? data.purchases : []);
    } catch (err) {
      setError(
        err?.response?.data?.errMessage ||
          err?.response?.data?.message ||
          "Failed to load slot purchases."
      );
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadPurchases();
  }, [loadPurchases]);

  const pendingCount = useMemo(
    () => purchases.filter((purchase) => purchase.status === "Pending").length,
    [purchases]
  );

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString();
  };

  const handleApprove = async (purchaseId) => {
    if (!window.confirm("Approve this slot purchase and credit seats to the school?")) {
      return;
    }

    setActionId(purchaseId);
    setError("");
    try {
      await apiClient.post(`/admin/school-slot-purchases/${purchaseId}/approve`);
      await loadPurchases();
    } catch (err) {
      setError(
        err?.response?.data?.errMessage ||
          err?.response?.data?.message ||
          "Failed to approve purchase."
      );
    } finally {
      setActionId("");
    }
  };

  const handleReject = async () => {
    if (!rejectingPurchase) return;
    setActionId(rejectingPurchase.id);
    setError("");
    try {
      await apiClient.post(`/admin/school-slot-purchases/${rejectingPurchase.id}/reject`, {
        reason: rejectReason,
      });
      setRejectingPurchase(null);
      setRejectReason("");
      await loadPurchases();
    } catch (err) {
      setError(
        err?.response?.data?.errMessage ||
          err?.response?.data?.message ||
          "Failed to reject purchase."
      );
    } finally {
      setActionId("");
    }
  };

  const openReceipt = (purchaseId) => {
    const baseUrl = resolveBaseURL();
    window.open(`${baseUrl}/admin/school-slot-purchases/${purchaseId}/receipt`, "_blank");
  };

  return (
    <Fragment>
      <div className="content-wrapper">
        <section className="content-header">
          <h1>
            School Portal
            <small>Slot Purchases</small>
          </h1>
          <ol className="breadcrumb">
            <li>
              <Link to="/admin">
                <i className="fa fa-dashboard" /> Home
              </Link>
            </li>
            <li className="active">Slot Purchases</li>
          </ol>
        </section>

        <section className="content">
          {error && (
            <div className="alert alert-danger">
              <button type="button" className="close" onClick={() => setError("")}>
                ×
              </button>
              {error}
            </div>
          )}

          <div className="box box-primary">
            <div className="box-header with-border">
              <h3 className="box-title">
                Slot Purchases{" "}
                {statusFilter === "Pending" && pendingCount > 0 ? `(${pendingCount})` : ""}
              </h3>
              <div className="box-tools">
                <select
                  className="form-control input-sm"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  {STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="box-body table-responsive">
              {loading ? (
                <Loader />
              ) : purchases.length === 0 ? (
                <p className="text-muted">No slot purchases found.</p>
              ) : (
                <table className="table table-bordered table-striped">
                  <thead>
                    <tr>
                      <th>School</th>
                      <th>Package</th>
                      <th>Exam</th>
                      <th>Type</th>
                      <th>Duration</th>
                      <th>Slots</th>
                      <th>Amount</th>
                      <th>Method</th>
                      <th>Status</th>
                      <th>Submitted</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchases.map((purchase) => (
                      <tr key={purchase.id}>
                        <td>
                          <div>{purchase.schoolName || "-"}</div>
                          <small className="text-muted">{purchase.schoolEmail}</small>
                        </td>
                        <td>{purchase.packageName}</td>
                        <td>{purchase.examType}</td>
                        <td>{purchase.subscriptionType || "Month"}</td>
                        <td>{purchase.duration || "-"} days</td>
                        <td>{purchase.slotCount}</td>
                        <td>₦{Number(purchase.amount || 0).toLocaleString()}</td>
                        <td>{purchase.paymentMethod}</td>
                        <td>
                          <span
                            className={`label label-${
                              purchase.status === "Approved"
                                ? "success"
                                : purchase.status === "Rejected"
                                  ? "danger"
                                  : "warning"
                            }`}
                          >
                            {purchase.status}
                          </span>
                        </td>
                        <td>{formatDate(purchase.submittedAt)}</td>
                        <td>
                          {purchase.hasReceipt && (
                            <button
                              type="button"
                              className="btn btn-xs btn-default"
                              onClick={() => openReceipt(purchase.id)}
                            >
                              Receipt
                            </button>
                          )}{" "}
                          {canReview && purchase.status === "Pending" && (
                            <Fragment>
                              <button
                                type="button"
                                className="btn btn-xs btn-success"
                                disabled={actionId === purchase.id}
                                onClick={() => handleApprove(purchase.id)}
                              >
                                Approve
                              </button>{" "}
                              <button
                                type="button"
                                className="btn btn-xs btn-danger"
                                disabled={actionId === purchase.id}
                                onClick={() => setRejectingPurchase(purchase)}
                              >
                                Reject
                              </button>
                            </Fragment>
                          )}
                          {purchase.rejectionReason ? (
                            <div>
                              <small className="text-danger">{purchase.rejectionReason}</small>
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </section>
      </div>

      {rejectingPurchase && (
        <div className="modal" style={{ display: "block", background: "rgba(0,0,0,0.4)" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <button
                  type="button"
                  className="close"
                  onClick={() => {
                    setRejectingPurchase(null);
                    setRejectReason("");
                  }}
                >
                  ×
                </button>
                <h4 className="modal-title">Reject Slot Purchase</h4>
              </div>
              <div className="modal-body">
                <p>
                  Reject purchase for <strong>{rejectingPurchase.schoolName}</strong> (
                  {rejectingPurchase.packageName})?
                </p>
                <textarea
                  className="form-control"
                  rows={3}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Optional rejection reason"
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-default"
                  onClick={() => {
                    setRejectingPurchase(null);
                    setRejectReason("");
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  disabled={actionId === rejectingPurchase.id}
                  onClick={handleReject}
                >
                  Reject
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default ListSchoolSlotPurchases;
